import { useEffect, useRef, useState } from "react";
import { useTurnstile } from "@/hooks/useTurnstile";
import { useTranslation } from "@/hooks/useTranslation";
import { getAsset } from "@/utils/assets";
import { TURNSTILE_SITE_KEY } from "@/config";
import classes from "./TurnstileWidget.module.scss";

export function TurnstileWidget() {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const { isScriptLoaded, setToken, setWidgetId } = useTurnstile();
  const [isInteractive, setIsInteractive] = useState(false);
  const t = useTranslation("_common");

  useEffect(() => {
    if (!isScriptLoaded || !containerRef.current || !window.turnstile) {
      return;
    }

    // Avoid rendering multiple times
    if (widgetIdRef.current) {
      return;
    }

    const id = window.turnstile.render(containerRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: (token: string) => {
        setToken(token);
        setIsInteractive(false);
      },
      "error-callback": () => {
        setToken(null);
        setIsInteractive(false);
      },
      "expired-callback": () => {
        setToken(null);
        setIsInteractive(false);
      },
      "before-interactive-callback": () => {
        setIsInteractive(true);
      },
      "after-interactive-callback": () => {
        setIsInteractive(false);
      },
      theme: "auto",
      size: "normal",
      appearance: "interaction-only",
    });

    widgetIdRef.current = id;
    setWidgetId(id);

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
        setWidgetId(null);
      }
    };
  }, [isScriptLoaded, setToken, setWidgetId]);

  return (
    <div className={classes.container}>
      {isInteractive && (
        <>
          <div className={classes.title}>
            <span>{t("donation-is-secure")}</span>
            <img
              src={getAsset("/images/lock-icon.svg")}
              alt=""
              width={18}
              height={18}
              className={classes.titleIcon}
            />
          </div>
          <p className={classes.message}>{t("security-check")}</p>
        </>
      )}
      <div ref={containerRef} />
    </div>
  );
}
